import React, { createContext, useContext, useMemo } from 'react';

import { RedisTreatment } from '../../../../lib/redis/types/RedisTreatment';
import { useSortAndFilterConfigContext } from './SortAndFilterConfigContext';
import { sortAndFilterData } from '../../../../modules/sortAndFilter/workers/sortAndFilter';
import { sortCommands } from '../common/SortCommands';
import { useGatsbyBuildCheck } from '../../../../hooks/useGatsbyBuildCheck';

interface Props {
    treatments: RedisTreatment[];
    children: React.ReactNode;
}

const SortAndFilterTreatmentsContext = createContext<RedisTreatment[]>([]);

const SortAndFilterTreatmentsProvider = ({ treatments, children }: Props) => {
    const config = useSortAndFilterConfigContext();
    const { build, initial } = useGatsbyBuildCheck();

    const presortedTreatments = useMemo(() => {
        /**
         * This doesn't evaluate to 'true' when window.location.pathname === /search-results
         */
        if (build || initial) {
            return sortAndFilterData([], [sortCommands[config.sorting.default!]], treatments) as [];
        }
        return treatments;
    }, [build, initial, treatments]);

    return (
        <SortAndFilterTreatmentsContext.Provider value={presortedTreatments as unknown as []}>
            {children}
        </SortAndFilterTreatmentsContext.Provider>
    );
};

export const useSortAndFilterTreatmentsContext = () => {
    const treatments = useContext(SortAndFilterTreatmentsContext) as RedisTreatment[];

    if (treatments === undefined) {
        throw new Error('useSortAndFilterTreatmentsContext must be used within a SortAndFilterTreatmentsProvider.');
    }

    return treatments;
};

export default SortAndFilterTreatmentsProvider;
